/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { colors } from "./colors";
import { StyledText } from "../assets/typography/typography";
import { SpinnerBlock } from "../module/common/Spinner";
import { effects } from "./effects";
import { IconButton } from "./IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FileUploadFormat } from "./forms/inputs/FileUploadInput";

const uploadListItemStyle = css`
  display: flex;
  flex-direction: row;
  padding: 16px;
  background-color: ${colors.whiteHover};
  box-shadow: ${effects.cardRest};
  border-radius: 8px;
  justify-content: space-between;
  align-items: center;
`;

const imageStyle = css`
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 6px;
  object-fit: cover;
`;

const pdfStyle = css`
  width: 32px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 4px;
  border-radius: 4px;
  background: linear-gradient(180deg, #f4f4f5 0%, #e4e4e7 100%);
  box-shadow: 0px 0px 0px 1px rgba(23, 23, 23, 0.08),
    0px 2px 4px 0px rgba(3, 7, 18, 0.06);
`;

const imageInfoContainer = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const imageAndInfoContainerStyle = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;

type Props = {
  file: File;
  remove: () => void;
  isLoading?: boolean;
  accept?: FileUploadFormat;
};

// Meant to be generic to use other places, so not hooking up with RHF
export const FileUploadWrapper = ({
  file,
  remove,
  isLoading,
  accept = FileUploadFormat.IMAGE,
}: Props) => {
  const [fileUrl, setFileUrl] = useState<string>("");

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setFileUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (isLoading) {
    return <SpinnerBlock />;
  }

  if (!file) {
    return null;
  }

  return (
    <div css={uploadListItemStyle}>
      <div css={imageAndInfoContainerStyle}>
        {accept === FileUploadFormat.VIDEO && (
          <video css={imageStyle} src={fileUrl} />
        )}
        {accept === FileUploadFormat.IMAGE && (
          <img css={imageStyle} src={fileUrl} alt="preview" />
        )}
        {accept === FileUploadFormat.PDF && (
          <div css={pdfStyle}>
            <FontAwesomeIcon icon={solid("file")} />
          </div>
        )}
        <div css={imageInfoContainer}>
          <StyledText
            color={colors.textPrimary}
            className="bodyTextRegular mb-0"
          >
            {file.name}
          </StyledText>
          <StyledText
            color={colors.textSecondary}
            className="bodySubtextRegular mb-0"
          >
            {file.size / 1000} KB
          </StyledText>
        </div>
      </div>
      <IconButton
        icon={<FontAwesomeIcon icon={solid("trash")} />}
        onClick={remove}
      />
    </div>
  );
};
